import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Pencil, Trash2, Plus, Wallet, Layers, Tag } from "lucide-react";
import { toast } from "sonner";

const LAYER_OPTIONS = [
  { value: "base_asphalt", label: "Bitumlu təməl (Base)" },
  { value: "binder_course", label: "Əlaqələndirici lay (Binder)" },
  { value: "wearing_course", label: "Aşınma qatı (Wearing)" },
];

const MATERIAL_KEY_HINTS = [
  "prime_coat_emulsion",
  "base_course_asphalt",
  "tack_coat_emulsion",
  "binder_course_asphalt",
  "wearing_course_asphalt",
];

export default function AdminTariffs() {
  const { user } = useAuth();
  const [tab, setTab] = useState("materials");
  const [materials, setMaterials] = useState([]);
  const [asphaltTypes, setAsphaltTypes] = useState([]);
  const [matDialog, setMatDialog] = useState({ open: false, editing: null, form: {} });
  const [atDialog, setAtDialog] = useState({ open: false, editing: null, form: {} });

  const load = async () => {
    const [m, a] = await Promise.all([
      api.get("/materials"),
      api.get("/asphalt-types"),
    ]);
    setMaterials(m.data);
    setAsphaltTypes(a.data);
  };

  useEffect(() => {
    load();
  }, []);

  if (!user) return null;
  if (user.role !== "superadmin")
    return <Navigate to="/" replace />;

  // ─── Material handlers ─────────────────────────────────────────────────
  const openMatNew = () =>
    setMatDialog({
      open: true,
      editing: null,
      form: { material_key: "", material_name: "", unit: "ton", unit_price_azn: "", notes: "" },
    });
  const openMatEdit = (m) =>
    setMatDialog({ open: true, editing: m, form: { ...m } });
  const submitMat = async () => {
    const { form, editing } = matDialog;
    try {
      const payload = {
        material_key: form.material_key,
        material_name: form.material_name,
        unit: form.unit || "ton",
        unit_price_azn: Number(form.unit_price_azn),
        notes: form.notes || "",
      };
      if (editing) {
        await api.patch(`/materials/${editing.id}`, payload);
        toast.success("Material yeniləndi");
      } else {
        await api.post("/materials", payload);
        toast.success("Material əlavə edildi");
      }
      setMatDialog({ open: false, editing: null, form: {} });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xəta");
    }
  };
  const deleteMat = async (m) => {
    if (!window.confirm(`'${m.material_name}' silinsin?`)) return;
    try {
      await api.delete(`/materials/${m.id}`);
      toast.success("Material silindi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xəta");
    }
  };

  // ─── Asphalt-type handlers ─────────────────────────────────────────────
  const openAtNew = () =>
    setAtDialog({
      open: true,
      editing: null,
      form: { layer_name: "wearing_course", code: "", label: "", density: 2.40 },
    });
  const openAtEdit = (a) =>
    setAtDialog({ open: true, editing: a, form: { ...a } });
  const submitAt = async () => {
    const { form, editing } = atDialog;
    try {
      const payload = {
        layer_name: form.layer_name,
        code: form.code,
        label: form.label,
        density: Number(form.density),
      };
      if (editing) {
        await api.patch(`/asphalt-types/${editing.id}`, payload);
        toast.success("Asfalt tipi yeniləndi");
      } else {
        await api.post("/asphalt-types", payload);
        toast.success("Asfalt tipi əlavə edildi");
      }
      setAtDialog({ open: false, editing: null, form: {} });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xəta");
    }
  };
  const deleteAt = async (a) => {
    if (!window.confirm(`'${a.label}' silinsin?`)) return;
    try {
      await api.delete(`/asphalt-types/${a.id}`);
      toast.success("Asfalt tipi silindi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xəta");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 text-orange-700 text-[11px] font-bold mb-2">
          <Tag className="w-3 h-3" /> SUPERADMIN
        </div>
        <h1 className="font-heading font-black text-4xl tracking-tighter">
          Tariflər və Qiymətlər
        </h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">
          Material qiymətləri və asfalt növlərinin sıxlıqlarını idarə edin.
          Burada edilən hər bir dəyişiklik bütün yeni mərhələ hesablamalarında
          dərhal tətbiq olunur.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { key: "materials", label: "Material qiymətləri", icon: Wallet, count: materials.length },
          { key: "asphalt", label: "Asfalt növləri", icon: Layers, count: asphaltTypes.length },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tariffs-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? "border-[#2563EB] text-[#2563EB]"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
            <span className="text-[10px] mono bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {/* ───────── MATERIALS TAB ───────── */}
      {tab === "materials" && (
        <div className="card-soft p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading font-bold text-lg">Material qiymət kataloqu</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                1 tonun AZN qiyməti — smeta hesablamasında istifadə olunur
              </p>
            </div>
            <Button
              onClick={openMatNew}
              className="bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] text-white"
              data-testid="material-add-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Yeni Material
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="materials-table">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-2 px-2 label-overline">Açar</th>
                  <th className="py-2 px-2 label-overline">Ad</th>
                  <th className="py-2 px-2 label-overline text-right">Qiymət (AZN/ton)</th>
                  <th className="py-2 px-2 label-overline">Qeyd</th>
                  <th className="py-2 px-2 label-overline text-right">Əməliyyatlar</th>
                </tr>
              </thead>
              <tbody>
                {materials.map((m) => (
                  <tr
                    key={m.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`material-row-${m.material_key}`}
                  >
                    <td className="py-3 px-2 mono text-xs text-slate-700">
                      {m.material_key}
                    </td>
                    <td className="py-3 px-2 font-semibold">{m.material_name}</td>
                    <td className="py-3 px-2 mono text-right font-bold text-lg">
                      {m.unit_price_azn.toLocaleString("az-AZ", {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                    <td className="py-3 px-2 text-xs text-slate-500">{m.notes || "—"}</td>
                    <td className="py-3 px-2 text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openMatEdit(m)}
                          data-testid={`material-edit-${m.material_key}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteMat(m)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          data-testid={`material-delete-${m.material_key}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {materials.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-slate-400">
                      Hələ material əlavə edilməyib
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ───────── ASPHALT TYPES TAB ───────── */}
      {tab === "asphalt" && (
        <div className="card-soft p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading font-bold text-lg">Asfalt növləri</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Mərhələ yaradılarkən sıxlıq (t/m³) bu siyahıdan götürülür
              </p>
            </div>
            <Button
              onClick={openAtNew}
              className="bg-gradient-to-r from-[#F97316] to-[#C2410C] text-white"
              data-testid="asphalt-add-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Yeni Asfalt Növü
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="asphalt-table">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-2 px-2 label-overline">Qat</th>
                  <th className="py-2 px-2 label-overline">Kod</th>
                  <th className="py-2 px-2 label-overline">Ad</th>
                  <th className="py-2 px-2 label-overline text-right">Sıxlıq (t/m³)</th>
                  <th className="py-2 px-2 label-overline text-right">Əməliyyatlar</th>
                </tr>
              </thead>
              <tbody>
                {asphaltTypes.map((a) => {
                  const layerLabel =
                    LAYER_OPTIONS.find((x) => x.value === a.layer_name)?.label ||
                    a.layer_name;
                  return (
                    <tr
                      key={a.id}
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`asphalt-row-${a.code}`}
                    >
                      <td className="py-3 px-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 text-[10px] mono uppercase font-bold">
                          {layerLabel}
                        </span>
                      </td>
                      <td className="py-3 px-2 mono text-xs">{a.code}</td>
                      <td className="py-3 px-2 font-semibold">{a.label}</td>
                      <td className="py-3 px-2 mono text-right font-bold">
                        {a.density.toFixed(2)}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <div className="inline-flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openAtEdit(a)}
                            data-testid={`asphalt-edit-${a.code}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteAt(a)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`asphalt-delete-${a.code}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {asphaltTypes.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-slate-400">
                      Hələ asfalt növü əlavə edilməyib
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ───── Material Dialog ───── */}
      <Dialog
        open={matDialog.open}
        onOpenChange={(o) =>
          setMatDialog((s) => ({ ...s, open: o, editing: o ? s.editing : null }))
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {matDialog.editing ? "Materialı redaktə et" : "Yeni material"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-overline">Material açarı</Label>
              <Input
                value={matDialog.form.material_key || ""}
                onChange={(e) =>
                  setMatDialog((s) => ({
                    ...s,
                    form: { ...s.form, material_key: e.target.value },
                  }))
                }
                disabled={!!matDialog.editing}
                placeholder="məs. wearing_course_asphalt"
                list="material-keys"
                data-testid="material-form-key"
              />
              <datalist id="material-keys">
                {MATERIAL_KEY_HINTS.map((k) => <option key={k} value={k} />)}
              </datalist>
              {!matDialog.editing && (
                <div className="text-[10px] text-slate-500 mt-1">
                  Sistem qatlarına uyğun açar adı (sonradan dəyişdirilə bilməz)
                </div>
              )}
            </div>
            <div>
              <Label className="label-overline">Ad</Label>
              <Input
                value={matDialog.form.material_name || ""}
                onChange={(e) =>
                  setMatDialog((s) => ({
                    ...s,
                    form: { ...s.form, material_name: e.target.value },
                  }))
                }
                data-testid="material-form-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-overline">Vahid</Label>
                <Input
                  value={matDialog.form.unit || "ton"}
                  onChange={(e) =>
                    setMatDialog((s) => ({
                      ...s,
                      form: { ...s.form, unit: e.target.value },
                    }))
                  }
                  data-testid="material-form-unit"
                />
              </div>
              <div>
                <Label className="label-overline">Qiymət (AZN/ton)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={matDialog.form.unit_price_azn || ""}
                  onChange={(e) =>
                    setMatDialog((s) => ({
                      ...s,
                      form: { ...s.form, unit_price_azn: e.target.value },
                    }))
                  }
                  data-testid="material-form-price"
                />
              </div>
            </div>
            <div>
              <Label className="label-overline">Qeyd</Label>
              <Input
                value={matDialog.form.notes || ""}
                onChange={(e) =>
                  setMatDialog((s) => ({
                    ...s,
                    form: { ...s.form, notes: e.target.value },
                  }))
                }
                placeholder="məs. EBK-1 / SMA-15"
                data-testid="material-form-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMatDialog({ open: false, editing: null, form: {} })}
            >
              Ləğv et
            </Button>
            <Button
              onClick={submitMat}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              data-testid="material-form-submit"
            >
              Yadda saxla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ───── Asphalt Dialog ───── */}
      <Dialog
        open={atDialog.open}
        onOpenChange={(o) =>
          setAtDialog((s) => ({ ...s, open: o, editing: o ? s.editing : null }))
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {atDialog.editing ? "Asfalt növünü redaktə et" : "Yeni asfalt növü"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-overline">Qat növü</Label>
              <Select
                value={atDialog.form.layer_name || "wearing_course"}
                onValueChange={(v) =>
                  setAtDialog((s) => ({
                    ...s,
                    form: { ...s.form, layer_name: v },
                  }))
                }
              >
                <SelectTrigger data-testid="asphalt-form-layer">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LAYER_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-overline">Kod</Label>
              <Input
                value={atDialog.form.code || ""}
                onChange={(e) =>
                  setAtDialog((s) => ({
                    ...s,
                    form: { ...s.form, code: e.target.value },
                  }))
                }
                placeholder="məs. SMA-15"
                data-testid="asphalt-form-code"
              />
            </div>
            <div>
              <Label className="label-overline">Ad</Label>
              <Input
                value={atDialog.form.label || ""}
                onChange={(e) =>
                  setAtDialog((s) => ({
                    ...s,
                    form: { ...s.form, label: e.target.value },
                  }))
                }
                placeholder="məs. ŞMA-15 (Taş Mastik Asfalt)"
                data-testid="asphalt-form-label"
              />
            </div>
            <div>
              <Label className="label-overline">Sıxlıq (t/m³)</Label>
              <Input
                type="number"
                step="0.01"
                min="1.5"
                max="3"
                value={atDialog.form.density || 2.4}
                onChange={(e) =>
                  setAtDialog((s) => ({
                    ...s,
                    form: { ...s.form, density: e.target.value },
                  }))
                }
                data-testid="asphalt-form-density"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAtDialog({ open: false, editing: null, form: {} })}
            >
              Ləğv et
            </Button>
            <Button
              onClick={submitAt}
              className="bg-[#F97316] hover:bg-[#C2410C] text-white"
              data-testid="asphalt-form-submit"
            >
              Yadda saxla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
